<template>
    <div class="variator" :class="{ 'variator--top-numbers': topNumbers }" @keydown="onKeyDown">
        <div class="variator__slider">
            <div v-if="minText && maxText" class="variator__slider-labels">
                <span class="variator__slider-label">{{ minText }}</span>
                <span class="variator__slider-label">{{ maxText }}</span>
            </div>
            <ol class="variator__slider-numberline">
                <li
                    v-for="(point, idx) in snapPoints"
                    :key="idx"
                    class="variator__slider-numberline-item"
                    :class="[
                        { 'variator__slider-numberline-item--active': point === model },
                        {
                            'variator__slider-numberline-item--inactive-smaller': idx < snapPoints.indexOf(model),
                        },
                    ]"
                    ref="sliderNumberlineItemElems"
                    :data-badge-number="point"
                    :data-test="`column-count-${point}`"
                >
                    <span v-if="topNumbers" class="variator__slider-numberline-item-label">{{ point }}</span>
                </li>
            </ol>
            <div class="variator__slider-bar" ref="sliderBarEl">
                <div class="variator__slider-bar-inner" ref="sliderBarInnerEl"></div>
                <div
                    class="variator__slider-bar-badge"
                    role="slider"
                    :aria-valuemin="minValue"
                    :aria-valuemax="maxValue"
                    :aria-valuenow="model"
                    ref="sliderBarBadgeEl"
                    :data-badge-number="model"
                    :aria-labelledby="labelledById"
                    :tabindex="0"
                    data-test="column-slider"
                >
                    {{ model }}
                    <WebpImage
                        v-show="shine && model > 1"
                        class="variator__slider-bar-shine"
                        src="/frontend/web/img/lotteries/variator/variator-shine.svg"
                        alt=""
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onUnmounted, nextTick, useTemplateRef, ref, onMounted, watch, computed } from "vue";
import WebpImage from "@sazka/design-system/src/image/webp-image.vue";
import { type Variator, useVariator } from "./use-variator";

const { snapPoints, labelledById } = defineProps<{
    snapPoints: number[];
    labelledById?: string;
    topNumbers?: boolean;
    minText?: string;
    maxText?: string;
    shine?: boolean;
}>();

const model = defineModel<number>();

const sliderBarEl = useTemplateRef("sliderBarEl");
const sliderBarInnerEl = useTemplateRef("sliderBarInnerEl");
const sliderBarBadgeEl = useTemplateRef("sliderBarBadgeEl");
const sliderNumberlineItemElems = ref<HTMLElement[] | null>(null);
const minValue = computed(() => snapPoints[0]);
const maxValue = computed(() => snapPoints[snapPoints.length - 1]);

if (model.value > maxValue.value) model.value = maxValue.value;

const updateBadgeCount = (count: number) => {
    model.value = count;
};

let variator: Variator;
const initVariator = async () => {
    await nextTick();

    variator = useVariator({
        containerEl: sliderBarEl.value,
        barEl: sliderBarInnerEl.value,
        badgeEl: sliderBarBadgeEl.value,
        sliderElems: sliderNumberlineItemElems.value,
        snapPoints: snapPoints,
        startValue: model.value,
        updateBadgeCount,
    });
};

watch(
    () => snapPoints.length,
    () => {
        if (variator) {
            variator.destroy();
            initVariator();
        }
    }
);

watch(model, (modelValue) => {
    variator.setCurrentValue(modelValue);
});

function decrementValue() {
    const currentIndex = snapPoints.indexOf(model.value);
    if (currentIndex > 0) {
        updateBadgeCount(snapPoints[currentIndex - 1]);
    }
}

function incrementValue() {
    const currentIndex = snapPoints.indexOf(model.value);
    if (currentIndex < snapPoints.length - 1) {
        updateBadgeCount(snapPoints[currentIndex + 1]);
    }
}

function onKeyDown(e: KeyboardEvent) {
    if (e.key === "ArrowLeft") {
        decrementValue();
    } else if (e.key === "ArrowRight") {
        incrementValue();
    }
}

const sliderBarElClickHandler = (e: MouseEvent) => {
    const barStartX = sliderBarEl.value.getBoundingClientRect().x;
    const clickX = e.clientX - barStartX;
    // get an array of dots centers
    const dotsXArray = sliderNumberlineItemElems.value.map(
        (el) => el.getBoundingClientRect().x - barStartX + el.clientWidth / 2
    );
    //find index of the closest dot center from clickX
    const closestIndex = dotsXArray.reduce(
        (closestIndex, num, index) =>
            Math.abs(num - clickX) < Math.abs(dotsXArray[closestIndex] - clickX) ? index : closestIndex,
        0
    );
    updateBadgeCount(snapPoints[closestIndex]);
};

onMounted(() => {
    initVariator();
    sliderBarEl.value.addEventListener("click", sliderBarElClickHandler);
});

onUnmounted(() => {
    variator?.destroy();
    sliderBarEl.value?.removeEventListener("click", sliderBarElClickHandler);
});
</script>
